import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Divider,
  Input,
  CircularProgress,
  TextField,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { SearchIcon } from "../../../Resources/Icons/icons";
import moment from "moment";
import dayjs from "dayjs";
import SmartRangePicker from "../../../UiComponents/SmartRangePicker/SmartRangePicker";
import MasterCommonTable from "../../../UiComponents/CommonTable/MasterCommonTable";
import { ToastError } from "../../../UiComponents/Toaster/Toast";
import {
  GetBulkUploadFiles,
  GetBulkUploadFileDownloadUrl,
} from "../../../Service/BulkUploadService";


// The bulk-upload "type" values written by the backend, with display labels.
export const BULK_UPLOAD_TYPE_OPTIONS = [
  { label: "User Payable", value: "Userpayable" },
  { label: "Branch Payable", value: "Branchpayable" },
  { label: "Commision Receivable", value: "Commisionreceivable" },
  { label: "Commision Received", value: "CommisionReceived" },
];

const TYPE_LABEL = BULK_UPLOAD_TYPE_OPTIONS.reduce(
  (acc, o) => ({ ...acc, [o.value]: o.label }),
  {},
);

// Renders the file name as a clickable link (click downloads the file).
const getFileNameClass = (row) =>
  row?.fileName && row.fileName !== "--" ? "file_link" : "";

const HISTORY_COLUMNS = [
  {
    id: "fileName",
    label: "File Name",
    placeMent: true,
    disablePadding: false,
    class: getFileNameClass,
    onClick: true,
  },
  {
    id: "uploaderName",
    label: "Uploaded By",
    placeMent: true,
    disablePadding: false,
  },
  { id: "totalRecords", label: "Total", placeMent: true, disablePadding: false },
  {
    id: "successfulRecords",
    label: "Success",
    placeMent: true,
    disablePadding: false,
  },
  {
    id: "failedRecords",
    label: "Failed",
    placeMent: true,
    disablePadding: false,
  },
  {
    id: "createdAtFormatted",
    label: "Created At",
    placeMent: true,
    disablePadding: false,
  },
];

const DEFAULT_QUERY = { page: 1, limit: 10, sort: "-createdAt", search: "" };

const BulkUploadHistoryDialog = ({
  open,
  setOpen,
  defaultType = "",
  title = "Bulk Upload History",
}) => {
  const [selectedType, setSelectedType] = useState(defaultType);
  const [dateRange, setDateRange] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [queryParam, setQueryParam] = useState(DEFAULT_QUERY);
  const [reloadKey, setReloadKey] = useState(0);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchHistory = async () => {
    setLoading(true);
    const body = {
      type: selectedType || undefined,
      startDate:
        dateRange && dateRange[0]
          ? dayjs(dateRange[0]).startOf("day").toISOString()
          : undefined,
      endDate:
        dateRange && dateRange[1]
          ? dayjs(dateRange[1]).endOf("day").toISOString()
          : undefined,
      search: queryParam.search || undefined,
      page: queryParam.page,
      limit: queryParam.limit,
      sort: queryParam.sort,
    };

    const res = await GetBulkUploadFiles(body);
    setLoading(false);
    if (res && !res.error) {
      const rows = (res.data || []).map((r) => ({
        _id: r._id,
        fileName: r.file?.originalFileName || r.file?.fileName || "--",
        uploaderName: r.uploaderName || "--",
        totalRecords: r.totalRecords ?? 0,
        successfulRecords: r.successfulRecords ?? 0,
        failedRecords: r.failedRecords ?? 0,
        createdAtFormatted: r.createdAt
          ? moment(r.createdAt).format("DD/MM/YYYY hh:mm A")
          : "--",
        data: r,
      }));
      setData(rows);
      setTotalCount(res.totalRecord || 0);
    } else {
      ToastError(res?.message || "Unable to load bulk upload history");
      setData([]);
      setTotalCount(0);
    }
  };

  // On open: preselect this page's type and load the first page.
  useEffect(() => {
    if (open) {
      setSelectedType(defaultType || "");
      setDateRange(null);
      setSearchText("");
      setQueryParam({ ...DEFAULT_QUERY });
      setReloadKey((k) => k + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultType]);

  useEffect(() => {
    if (open && reloadKey > 0) {
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  // Debounced search.
  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => {
      setQueryParam((prev) => ({ ...prev, search: searchText, page: 1 }));
      setReloadKey((k) => k + 1);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const handleClose = () => setOpen(false);

  const handleDownload = async (row) => {
    const id = row?._id || row?.data?._id;
    if (!id) return;
    const res = await GetBulkUploadFileDownloadUrl(id);
    if (res && !res.error && res.data?.downloadURL) {
      window.open(res.data.downloadURL, "_blank");
    } else {
      ToastError(res?.message || "Unable to download file");
    }
  };

  // Filters apply immediately on change.
  const applyFilters = () => {
    setQueryParam((prev) => ({ ...prev, page: 1 }));
    setReloadKey((k) => k + 1);
  };

  const handleTypeChange = (value) => {
    // Type is always selected - ignore attempts to clear it.
    if (!value) return;
    setSelectedType(value);
    applyFilters();
  };

  const handleDateChange = (range) => {
    setDateRange(range);
    applyFilters();
  };

  const paginationReset = `${selectedType}|${dateRange?.[0] || ""}|${
    dateRange?.[1] || ""
  }|${queryParam.search || ""}`;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {`${TYPE_LABEL[selectedType] || "Bulk Upload"} — Bulk Upload History`}
        </Typography>
        <IconButton aria-label="close" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 2 }}>
        {/* Filters (apply on change) + bordered search */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          <TextField
            select
            size="small"
            label="Type"
            value={selectedType}
            onChange={(e) => handleTypeChange(e.target.value)}
            sx={{
              minWidth: 220,
              "& .MuiOutlinedInput-root": {
                height: 40,
                borderRadius: "8px",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "1px solid #D0D5DD !important",
              },
            }}
          >
            {BULK_UPLOAD_TYPE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: 40,
              minWidth: 260,
              px: 1,
              border: "1px solid #D0D5DD",
              borderRadius: "8px",
              backgroundColor: "#fff",
            }}
          >
            <SmartRangePicker
              value={dateRange}
              onChange={handleDateChange}
              format="DD/MM/YYYY"
              variant="borderless"
              style={{ width: "100%", padding: 0 }}
              popupStyle={{ zIndex: 2000 }}
            />
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              maxWidth: "300px",
              height: "40px",
              px: 1.5,
              border: "1px solid #D0D5DD",
              borderRadius: "8px",
              backgroundColor: "#fff",
              transition: "border-color 0.15s ease, box-shadow 0.15s ease",
              "&:focus-within": {
                borderColor: "#008CBA",
                boxShadow: "0 0 0 3px rgba(0,140,186,0.12)",
              },
            }}
          >
            <SearchIcon sx={{ fontSize: "20px", color: "#667085", mr: 1 }} />
            <Input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              disableUnderline
              placeholder="Search file / type"
              sx={{ width: "100%", fontSize: "14px" }}
            />
          </Box>
        </Box>

        <Divider sx={{ backgroundColor: "#EAECF0", mb: 1 }} />

        {/* Stable-height table area with a soft loading overlay so changing a
            filter doesn't collapse/jump the dialog. */}
        <Box sx={{ position: "relative", minHeight: 360 }}>
          <MasterCommonTable
            tableConfig={{ type: "bulkUploadHistory" }}
            data={data}
            columns={HISTORY_COLUMNS}
            handleOnClickBehaviour={handleDownload}
            isServerSide={true}
            tableTotalCount={totalCount}
            tableOutputEvent={(params) => {
              setQueryParam((prev) => ({
                ...prev,
                page: params.page,
                limit: params.limit,
                sort: params.sort || prev.sort,
              }));
              setReloadKey((k) => k + 1);
            }}
            paginationReset={paginationReset}
          />

          {loading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255,255,255,0.6)",
                zIndex: 2,
                borderRadius: "8px",
              }}
            >
              <CircularProgress size={32} sx={{ color: "#008CBA" }} />
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BulkUploadHistoryDialog;
